import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ConsultantLauncher } from './consultant/ConsultantLauncher';
import { ConsultantPanel } from './consultant/ConsultantPanel';
import { useBooking } from './BookingModal';
import { briefFields, consultantIntro, quickActions } from '../data/consultant';
import { openingReplies } from '../lib/consultant/engine';
import { askConsultant } from '../lib/consultant/api';
import { createSession } from '../lib/consultant/types';
import type { ChatMessage, ConsultantAction, ConsultantReply, ConsultantSession } from '../lib/consultant/types';
const STORAGE_KEY = 'morvix-ai-conversation';
type Stored = {
  messages: ChatMessage[];
  session: ConsultantSession;
};
function loadStored(): Stored | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!Array.isArray(parsed.messages) || !parsed.session) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Morvix AI — the studio's Creative Consultant. Mounted once at app level;
 * remembers the visitor's conversation for the whole session.
 */
export function MorvixConsultant() {
  const navigate = useNavigate();
  const {
    openBooking
  } = useBooking();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [session, setSession] = useState<ConsultantSession>(createSession);
  const [typing, setTyping] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const idRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const nextId = () => {
    idRef.current += 1;
    return `m${idRef.current}-${Date.now().toString(36)}`;
  };

  /* Restore the session conversation (survives refreshes within the visit). */
  useEffect(() => {
    const stored = loadStored();
    if (stored && stored.messages.length > 0) {
      setMessages(stored.messages);
      setSession(stored.session);
      setGreeted(true);
      idRef.current = stored.messages.length;
    }
  }, []);
  useEffect(() => {
    if (messages.length === 0) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages,
        session
      }));
    } catch {
      /* storage unavailable — conversation simply lives in memory */
    }
  }, [messages, session]);
  useEffect(() => () => timersRef.current.forEach(timer => window.clearTimeout(timer)), []);
  const schedule = (fn: () => void, delay: number) => {
    const timer = window.setTimeout(fn, delay);
    timersRef.current.push(timer);
  };

  /** Streams replies in with a natural, staggered typing rhythm. */
  const deliver = useCallback((replies: ConsultantReply[], startDelay = 480) => {
    let elapsed = startDelay;
    setTyping(true);
    replies.forEach((reply, index) => {
      const dwell = Math.min(1400, 420 + reply.text.length * 3.2);
      elapsed += index === 0 ? 0 : dwell * 0.5;
      schedule(() => {
        setMessages(current => [...current, {
          id: nextId(),
          role: 'assistant',
          text: reply.text,
          actions: reply.actions
        }]);
        if (index === replies.length - 1) setTyping(false);else setTyping(true);
      }, elapsed);
      elapsed += dwell;
    });
  }, []);
  const send = useCallback((value: string, display?: string) => {
    const shown = (display ?? value).trim();
    if (!shown) return;
    setMessages(current => [...current, {
      id: nextId(),
      role: 'user',
      text: shown
    }]);
    setTyping(true);
    const historySnapshot = messages;
    const sessionSnapshot = session;
    void (async () => {
      const result = await askConsultant(value, historySnapshot, sessionSnapshot);
      setSession(result.session);
      deliver(result.replies, 520);
    })();
  }, [deliver, messages, session]);
  const handleAction = useCallback((action: ConsultantAction) => {
    if (action.kind === 'booking') {
      openBooking();
      return;
    }
    if (action.kind === 'brief') {
      send('start a project', action.label);
      return;
    }
    if (action.kind === 'route') {
      navigate(action.value);
      return;
    }
    send(action.value);
  }, [navigate, openBooking, send]);
  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!greeted) {
      setGreeted(true);
      deliver(openingReplies(consultantIntro, quickActions), 700);
    }
    setOpen(true);
  };
  const briefProgress = session.flow === 'brief' ? Math.min(1, session.briefStep / briefFields.length) : null;
  return <>
      {!open ? <ConsultantLauncher open={open} unread={!greeted} onClick={toggle} data-id="element-330" data-mp="ffpcmbnpgo22" /> : null}
      <AnimatePresence data-id="element-331" data-mp="tf85jx7y3sts">
        {open ? <ConsultantPanel messages={messages} typing={typing} briefProgress={briefProgress} onSend={value => send(value)} onAction={handleAction} onClose={() => setOpen(false)} data-id="element-332" data-mp="tg9t3165e7se" /> : null}
      </AnimatePresence>
    </>;
}